// ** React Imports
import { createContext, useEffect, useState } from "react";

import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
// ** Axios
import axios from "axios";

// ** Config
import { authConfig } from "src/configs/auth";
import { pathForAuthRoute, searchSuffixWithoutToken } from "src/@core/utils/urlQueryPreservation";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

// ** Defaults
const defaultProvider = {
  user: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  isInitialized: false,
  login: () => Promise.resolve(),
  requestOtp: () => Promise.resolve(),
  otpLogin: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  setIsInitialized: () => Boolean,
  resetPassword: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
};
const AuthContext = createContext(defaultProvider);

// function useQuery() {
//   const { search } = useLocation();

//   return useMemo(() => new URLSearchParams(search), [search]);
// }

const stripTrailingSlash = (str) => {
  return str.endsWith("/") ? str.slice(0, -1) : str;
};

const AuthProvider = ({ children }) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user);
  const [loading, setLoading] = useState(defaultProvider.loading);
  const [isInitialized, setIsInitialized] = useState(defaultProvider.isInitialized);
  const [, setSearch] = useSearchParams();
  // ** Hooks

  const navigate = useNavigate();
  const location = useLocation();
  // console.log("AuthProvider", location);

  // Persist `token` from URL, then strip it (keep other params e.g. mode=iframe). Runs before initAuth below.
  // Skip the reset-password page: there `token` is a password reset token (not an SSO/auth token) and must
  // stay in the URL so the reset page can read it before submitting.
  useEffect(() => {
    if (stripTrailingSlash(location.pathname) === "/reset-password") return;

    const params = new URLSearchParams(location.search);
    let token = params.get("token");
    if (!token) return;
    // Integrators sometimes append `?mode=iframe` after the JWT instead of `&mode=iframe`, which corrupts `token` in URLSearchParams.
    const qAt = token.indexOf("?");
    if (qAt !== -1) {
      const tail = token.slice(qAt + 1);
      token = token.slice(0, qAt);
      new URLSearchParams(tail).forEach((value, key) => {
        if (!params.has(key)) params.set(key, value);
      });
    }
    window.localStorage.setItem(authConfig.storageTokenKeyName, token);
    params.delete("token");
    setSearch(params, { replace: true });
  }, [location.search, location.pathname, setSearch]);

  useEffect(() => {
    const initAuth = async () => {
      const publicPages = ["/login", "/register", "/forgot-password", "/reset-password", "/otp-login"];
      // console.log("AuthProvider location.pathname", location.pathname);
      if (publicPages.includes(stripTrailingSlash(location.pathname))) {
        setLoading(false);
        return;
      }

      const redirectURL = location.pathname + searchSuffixWithoutToken(location.search);
      setIsInitialized(true);
      setLoading(true);
      const storedToken = window.localStorage.getItem(authConfig.storageTokenKeyName);
      if (storedToken) {
        await axios
          .get(authConfig.meEndpoint, {
            headers: {
              Authorization: storedToken,
            },
          })
          .then(async (response) => {
            setLoading(false);
            setUser({ ...response.data.userData });
          })
          .catch(() => {
            localStorage.removeItem("userData");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("accessToken");
            setUser(null);
            setLoading(false);

            navigate(`/login?returnUrl=${encodeURIComponent(redirectURL)}`);
          });
      } else {
        setLoading(false);
        navigate(`/login?returnUrl=${encodeURIComponent(redirectURL)}`);
      }
    };
    initAuth();
  }, []);

  const handleLogin = (params, errorCallback) => {
    axios
      .post(authConfig.loginEndpoint, params)
      .then(async (res) => {
        window.localStorage.setItem(authConfig.storageTokenKeyName, res.data.accessToken);
        getAcademyMe(res.data.accessToken);
      })
      .catch((err) => {
        if (errorCallback) errorCallback(err);
      });
  };

  const getAcademyMe = async (accessToken) => {
    try {
      axios
        .get(authConfig.meEndpoint, {
          headers: {
            Authorization: accessToken, //window.localStorage.getItem(authConfig.storageTokenKeyName),
          },
        })
        .then(async (response) => {
          const qs = new URLSearchParams(location.search);
          const returnUrlParam = qs.get("returnUrl");
          setUser({ ...response.data.userData });
          window.localStorage.setItem("userData", JSON.stringify(response.data.userData));
          const redirectURL = returnUrlParam && returnUrlParam !== "/" ? returnUrlParam : "/";

          navigate(redirectURL);
        });
    } catch (err) {
      // console.log("getAcademyMe err", err);
    }
  };

  const handleForgotPassword = (params, errorCallback, successCallback) => {
    axios
      .post(authConfig.forgotPasswordEndpoint, params)
      .then(async (res) => {
        if (successCallback) successCallback(res);
      })

      .catch((err) => {
        if (errorCallback) errorCallback(err);
      });
  };

  const handleResetPassword = (params, errorCallback, successCallback) => {
    axios
      .post(authConfig.resetPasswordEndpoint, params)
      .then(async (res) => {
        if (successCallback) successCallback(res);
      })
      .catch((err) => {
        if (errorCallback) errorCallback(err);
      });
  };

  const handleChangePassword = (params, errorCallback, successCallback) => {
    axios
      .post(authConfig.changePasswordEndpoint, params)
      .then(async (res) => {
        if (successCallback) successCallback(res);
      })
      .catch((err) => {
        if (errorCallback) errorCallback(err);
      });
  };

  const handleLogout = () => {
    setUser(null);
    setIsInitialized(false);
    window.localStorage.removeItem("userData");
    window.localStorage.removeItem(authConfig.storageTokenKeyName);
    navigate(pathForAuthRoute("/login", location.search));
  };

  const handleRequestOtp = (params, errorCallback, successCallback) => {
    axios
      .post(authConfig.requestOTPEndpoint, params)
      .then(async (res) => {
        if (successCallback) successCallback(res);
      })
      .catch((err) => {
        if (errorCallback) errorCallback(err);
      });
  };

  const handleOtpLogin = (params, errorCallback) => {
    axios
      .post(authConfig.otpLoginEndpoint, params)
      .then(async (res) => {
        window.localStorage.setItem(authConfig.storageTokenKeyName, res.data.accessToken);
        getAcademyMe(res.data.accessToken);
      })
      .catch((err) => {
        if (errorCallback) errorCallback(err);
      });
  };

  const values = {
    user,
    loading,
    setUser,
    setLoading,
    isInitialized,
    setIsInitialized,
    login: handleLogin,
    otpLogin: handleOtpLogin,
    requestOtp: handleRequestOtp,
    logout: handleLogout,
    forgotPassword: handleForgotPassword,
    resetPassword: handleResetPassword,
    changePassword: handleChangePassword,

    //register: handleRegister
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
